var _ = require('underscore');
var SecureMessageWidget = require('./../secure-message/SecureMessageWidget');
var AppointmentTypeParentBehavior = require('./../behaviors/AppointmentTypeParentBehavior');
var AppointmentTypeDataBehavior = require('./../appointment-type/AppointmentTypeDataBehavior');
var wizardUtils = require('./../wizard/wizardUtils');
var launcherAppInterface = require('./../launcherApplicationInterface');

var VIEW_NAME = 'appointmentsOnDayListWidget';

/**
 * Customizations to the SecureMessageWidget specific to the appointments on a specific day list page.
 * @class AppointmentsOnDayListWidget
 * @constructor
 * @extends SecureMessageWidget
 */
module.exports = SecureMessageWidget.extend({
  moduleNamespace: VIEW_NAME,

  headerMessageKey: VIEW_NAME + '.header',
  noDataMessageKey: VIEW_NAME + '.noMessages',
  loadingMessageKey: VIEW_NAME + '.loading',
  fetchFailedMessageKey:  VIEW_NAME + '.fetchFailed',

  /**
   * Adds attributes to the the top level element (this is to support existing behavioral tests that query these values).
   * @returns {{data-test-appointments-on-day: String}} the test selector including the date of appts being displayed.
   */
  attributes: function() {
    return {
      'data-test-appointments-on-day': this._appointmentDate
    };
  },

  behaviors: _.extend({}, SecureMessageWidget.prototype.behaviors, {
    appointmentTypeParentBehavior: AppointmentTypeParentBehavior,
    appointmentType: {
      behavior: AppointmentTypeDataBehavior,
      updateEvents: 'behaviors.secureMessage.data.privateCollection:add',
      ids: { property: 'behaviors.secureMessage.data:appointmentDTO.externalAppointment.type'}
    },
    parentAppointmentType: {
      behavior: AppointmentTypeDataBehavior,
      ids: { property: 'behaviors.appointmentType.data:parentExternalId'}
    },
    grandparentAppointmentType: {
      behavior: AppointmentTypeDataBehavior,
      ids: { property: 'behaviors.parentAppointmentType.data:parentExternalId'}
    }
  }),

  /**
   * This is used by list view to determine which models to render.  In this case we do some client-side filtering
   * to remove the appointments that cannot be registered.
   * @method modelsToRender
   * @return {SecureMessageModel[]} the models to actually render by this list view.
   * @override
   */
  modelsToRender: function() {
    var allModels = SecureMessageWidget.prototype.modelsToRender.apply(this);

    var thisWidget = this;
    return _.filter(allModels, function(model) {
      var appointment = model.get('appointmentDTO.externalAppointment');
      var appointmentTypeId = appointment.type;
      var appointmentType = thisWidget.getBehavior('appointmentType').data.getModel(appointmentTypeId);
      return wizardUtils.isAppointmentPreRegisterable(appointment, appointmentType);
    });
  },

  /**
   * The MixedMessageCriteria selecting the messages to display.
   *
   * @property criteria {Object}
   *   @property criteria.isPatient {Boolean} username restrictions apply to patient usernames only.
   *   @property criteria.vecnaIds {Array} Array of vecnaId restrictions
   *   @property criteria.appointmentCriteria {Object} AppointmentMessageCriteria - restrictions on appointment messages.
   *     @property criteria.appointmentCriteria.externalAppointmentCriteria {Object} restrictions on the appointment itself.
   */
  criteria: function() {
    var startMoment = this._appointmentDate.clone().startOf('day');
    var endMoment = this._appointmentDate.clone().endOf('day');
    var criteria = {
      appointmentCriteria: {
        externalAppointmentCriteria: {
          start: startMoment.valueOf(),
          end: endMoment.valueOf(),
          cancelled: false
        }
      },
      // username restrictions apply to patient usernames only
      isPatient: !this._userIsStaff,
      // no vecnaId restrictions
      vecnaIds: this._userIsStaff ? [ this._patientVecnaId ] : null,
      pagingAndSorting: {
        pageNumber: 0,
        orderingMap: {
          actionDate: true
        },
        pageSize: 7
      }
    };

    if (this._loadAllAppointments) {
      delete criteria.pagingAndSorting.pageSize;
    }

    criteria.appointmentCriteria.externalAppointmentCriteria.checkedIn = false;
    criteria.appointmentCriteria.externalAppointmentCriteria.cancelled = false;

    if (!launcherAppInterface.isAtHospital()) {
      criteria.appointmentCriteria.externalAppointmentCriteria.preregistered = false;
    }
    return criteria;
  },

  /**
   * @method initialize
   * @param options {Object}
   *   @param options.userIsStaff {Boolean} true if the user is a staff user, false otherwise.
   *   @param options.patientVecnaId {String} the vecna id of the patient to lookup messages for.
   *   @param options.appointmentDate {Moment} Moment wrapper around the date to lookup appointments for.
   *   @param options.loadAllAppointments {Boolean} true if all appointments should be displayed (no page size).
   *                                                false if appointments should be limited to 7 per page.
   *   @param options.criteria {Object} the base criteria to use for retrieving the collection of messages.
   *   @param options.noDataMessageKey {String} i18n key to display when no messages are found
   *   @param options.loadingMessageKey {String} i18n key to display while the list is loading
   *   @param options.headerMessageKey {String} i18n key to display on the top of the view
   *   @param options.fetchFailedMessageKey {String} i18n key to display if the fetch fails
   *   @param options.hideIfEmpty {boolean} if true, hide the widget entirely if there are no results (defaults to false).
   *   @param options.selectable {Boolean} true if each item should render a select checkbox, false otherwise.
   *   @param options.defaultSelected {Boolean} the initial value for the selection of each item.
   *   @param options.itemContext {Object} the context to pass to the item views.
   *     @param options.itemContext.selectable {Boolean} true if each item should render a select checkbox, false otherwise.
   *     @param options.itemContext.defaultSelected {Boolean} the initial value for the selection of each item.
   */
  initialize: function(options) {
    options = options || {};
    this._patientVecnaId = options.patientVecnaId;
    this._userIsStaff = options.userIsStaff;
    this._appointmentDate = this._appointmentDate || options.appointmentDate;
    if (!this._appointmentDate) {
      throw 'appointmentDate option is required';
    }
    this._loadAllAppointments = options.loadAllAppointments;
    options.itemContext = options.itemContext || {};
    options.itemContext.hideRegisterButton = true;

    SecureMessageWidget.prototype.initialize.apply(this, arguments);

    this.listenTo(this.getBehavior('appointmentType'), 'fetched', this.update);
    this.listenTo(this.getBehavior('parentAppointmentType'), 'fetched', this.update);
    this.listenTo(this.getBehavior('grandparentAppointmentType'), 'fetched', this.update);
  },

  /**
   * @method hasFetchedData
   * @return {Boolean} true if this view has already fetched and it was successful and there is data to display.  False otherwise.
   */
  hasFetchedData: function() {
    var secureMessageFetchSuccess = this.getBehavior('secureMessage').get('fetchSuccess');
    var appointmentTypeFetchSuccess = this.getBehavior('appointmentType').get('fetchSuccess');
    var parentAppointmentTypeFetchSuccess = this.getBehavior('parentAppointmentType').get('fetchSuccess');
    var grandParentAppointmentTypeFetchSuccess = this.getBehavior('grandparentAppointmentType').get('fetchSuccess');

    return secureMessageFetchSuccess && appointmentTypeFetchSuccess
        && parentAppointmentTypeFetchSuccess && grandParentAppointmentTypeFetchSuccess && this.get('hasData');
  },

  /**
   * @method hasOnlyOneAppointmentToSelect
   * @returns {boolean} true if has only one appointment to select
   */
  hasOnlyOneAppointmentToSelect: function() {
    return this.hasFetchedData() && this.getNumberOfMessages() <= 1;
  }
});
